import { useEffect, useState } from "react";
import { AlertCircle, Loader2, PlayCircle, Video } from "lucide-react";
import { ApiService } from "../../../../services/api";
import { MyVideoProgress as MyVideoProgressData } from "../../../../types/student-report";

// Raporlarım → Videolar: ders başına açtığı videolar. Oynatıcı izleme süresini kaydetmiyor; "açılan" = en az bir kez açılmış.
const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long" }) : "-");

const barColor = (percent: number) => (percent >= 70 ? "bg-green-500" : percent >= 30 ? "bg-blue-500" : percent > 0 ? "bg-orange-500" : "bg-gray-300");

export default function MyVideoProgress() {
  const [data, setData] = useState<MyVideoProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    ApiService.getMyVideoProgress()
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err: any) => { if (!cancelled) setError(err?.message || "Video ilerlemesi yüklenemedi"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-16 h-16 text-red-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
        <p className="text-gray-500 max-w-md">{error}</p>
      </div>
    );
  }

  if (!data || data.lessons.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Video className="w-16 h-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Video Bulunamadı</h3>
        <p className="text-gray-500 max-w-md">Erişebildiğin derslerde henüz video yok.</p>
      </div>
    );
  }

  const { summary } = data;

  return (
    <div className="w-full space-y-4 sm:space-y-6 pb-4 sm:pb-6 px-2 sm:px-4 lg:px-0">
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
          <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Video İlerlemesi
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {[
            { label: "Açılan video", value: summary.openedVideos, color: "text-blue-600" },
            { label: "Toplam video", value: summary.totalVideos, color: "text-gray-900" },
            { label: "İlerleme", value: `%${summary.percent}`, color: "text-green-600" },
            { label: "Başlanan ders", value: `${summary.lessonsStarted} / ${summary.lessonCount}`, color: "text-purple-600" },
          ].map((tile) => (
            <div key={tile.label} className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
              <div className={`text-lg sm:text-xl font-bold ${tile.color}`}>{tile.value}</div>
              <div className="text-xs text-gray-500 text-center leading-tight">{tile.label}</div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">Bir videoyu açtığında burada sayılır; videonun ne kadarını izlediğin henüz ölçülmüyor.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
          <Video className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Ders Bazında
        </h3>
        <div className="divide-y divide-gray-100">
          {data.lessons.map((lesson) => (
            <div key={lesson.lessonId} className="py-2.5 sm:py-3">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="text-sm font-medium text-gray-900 truncate">{lesson.lessonTitle}</span>
                <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                  {lesson.openedVideos} / {lesson.totalVideos} video · %{lesson.percent}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full transition-all duration-300 ${barColor(lesson.percent)}`} style={{ width: `${Math.max(lesson.percent, lesson.openedVideos ? 2 : 0)}%` }} />
              </div>
              <div className="mt-1 text-xs text-gray-500">{lesson.lastOpenedAt ? `Son açtığın: ${formatDate(lesson.lastOpenedAt)}` : "Henüz video açmadın"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
